import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { getRequest, postRequest } from '../../../../../Helper/ApiClient';
import type { BoothPanelProps } from '../panels';
import StarRating from './StarRating/StarRating';
import styles from './FeedbackPanel.module.scss?module';

/** One question as GET /api/virtual-trade-show/feedbacks?exhibitor_id= returns it. */
interface IVtsFeedbackQuestion {
  id: number;
  number: number;
  question: string;
  type: 'rating' | 'text' | 'bool';
}

/** One answer as POST /api/virtual-trade-show/save-feedback takes it in answersJson. */
interface IVtsFeedbackAnswer {
  vtsQuestion_id: number;
  value: string | number;
}

/** Stars per rating question, as in the old popup. */
const RATING_MAX = 4;
const TEXT_MAX_LENGTH = 2000;
const SKELETON_ROWS = 3;

function isAnswered(answer: IVtsFeedbackAnswer | undefined): boolean {
  if (!answer) {
    return false;
  }

  return typeof answer.value === 'number'
    ? answer.value > 0
    : answer.value.trim() !== '';
}

/**
 * Give feedback: the survey of the show for this exhibitor, as in the old popup. The questions
 * come from the ERP; once the user has answered, the endpoint returns none and the panel thanks
 * them. Saving grants the feedback reward. Questions of type bool are not asked, like before.
 */
export default function FeedbackPanel({
  exhibitor,
  tab,
  onBadge,
  track,
}: BoothPanelProps) {
  const questionsRequest = getRequest<IVtsFeedbackQuestion[]>(
    '/api/virtual-trade-show/feedbacks?exhibitor_id=' + exhibitor.id
  );
  const saveMutation = postRequest('/api/virtual-trade-show/save-feedback');
  const [answers, setAnswers] = useState<Record<number, IVtsFeedbackAnswer>>(
    {}
  );
  const [showMissing, setShowMissing] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    onBadge(tab, null);
  }, [tab, onBadge]);

  const questions = (
    Array.isArray(questionsRequest.data) ? questionsRequest.data : []
  ).filter((question) => question.type !== 'bool');
  const answeredCount = questions.filter((question) =>
    isAnswered(answers[question.id])
  ).length;
  const isComplete = questions.length > 0 && answeredCount === questions.length;

  const setAnswer = (
    question: IVtsFeedbackQuestion,
    value: string | number
  ) => {
    setAnswers((prev) => ({
      ...prev,
      [question.id]: { vtsQuestion_id: question.id, value },
    }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saveMutation.isPending) {
      return;
    }
    if (!isComplete) {
      setShowMissing(true);
      return;
    }
    const formData = new FormData();
    formData.append('exhibitor_id', String(exhibitor.id));
    formData.append('answersJson', JSON.stringify(answers));
    saveMutation.mutate(formData, {
      onSuccess: () => {
        setSent(true);
        setAnswers({});
        track('feedback_sent', { questions: questions.length });
        // Answered once: the endpoint now returns no questions.
        questionsRequest.refetch();
      },
    });
  };

  if (questionsRequest.isPending) {
    return (
      <div className={styles.panel} aria-busy="true">
        <ul className={styles.questionList} aria-label="Loading questions">
          {Array.from({ length: SKELETON_ROWS }, (_, index) => (
            <li key={index} className={styles.skeletonRow} />
          ))}
        </ul>
      </div>
    );
  }

  if (questionsRequest.isError) {
    return (
      <div className={styles.panel}>
        <p className={styles.message} role="alert">
          The feedback questions could not be loaded. Please try again later.
        </p>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className={styles.panel}>
        <section className={styles.card}>
          <h2 className={styles.sectionLabel}>Your feedback</h2>
          <p className={styles.message} role="status">
            {sent
              ? `Thank you for your feedback for ${exhibitor.name}.`
              : `You have already given your feedback for ${exhibitor.name}. Thank you.`}
          </p>
        </section>
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <form className={styles.card} onSubmit={handleSubmit} noValidate>
        <h2 className={styles.sectionLabel}>
          Your feedback for {exhibitor.name}
        </h2>
        <p className={styles.intro}>
          Please answer all questions to submit your feedback.
        </p>
        <ol className={styles.questionList}>
          {questions.map((question) => {
            const answer = answers[question.id];
            const missing = showMissing && !isAnswered(answer);

            return (
              <li key={question.id} className={styles.question}>
                <p className={styles.questionText} id={questionDomId(question)}>
                  {question.question}
                </p>
                {question.type === 'rating' ? (
                  <StarRating
                    value={typeof answer?.value === 'number' ? answer.value : 0}
                    max={RATING_MAX}
                    labelledBy={questionDomId(question)}
                    invalid={missing}
                    onChange={(value) => setAnswer(question, value)}
                  />
                ) : (
                  <textarea
                    className={styles.textarea}
                    rows={3}
                    maxLength={TEXT_MAX_LENGTH}
                    value={
                      typeof answer?.value === 'string' ? answer.value : ''
                    }
                    aria-labelledby={questionDomId(question)}
                    aria-invalid={missing || undefined}
                    onChange={(event) =>
                      setAnswer(question, event.target.value)
                    }
                  />
                )}
                {missing && (
                  <p className={styles.errorText}>
                    Please answer this question.
                  </p>
                )}
              </li>
            );
          })}
        </ol>
        <div className={styles.footer}>
          <span className={styles.counter} aria-live="polite">
            {answeredCount} of {questions.length} answered
          </span>
          <button
            type="submit"
            className={styles.primaryButton}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? 'Sending …' : 'Send feedback'}
          </button>
        </div>
        {showMissing && !isComplete && (
          <p className={styles.errorText} role="alert">
            Please answer all questions.
          </p>
        )}
        {saveMutation.isError && (
          <p className={styles.errorText} role="alert">
            Could not send your feedback. Please try again.
          </p>
        )}
      </form>
    </div>
  );
}

function questionDomId(question: IVtsFeedbackQuestion): string {
  return 'booth-feedback-question-' + question.id;
}
